import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import {
  cancelDocumentWorkflow,
  createDocumentWorkflow,
  extractPdfText,
  getActiveDocumentWorkflow,
  getDocumentWorkflow,
  listDocumentPacks,
} from "@/api/documents";
import {
  clearDocumentWorkspaceCaches,
  type DocumentFormDraft,
  readDocumentDraft,
  readDocumentGenerationActivities,
  writeDocumentDraft,
  writeDocumentGenerationActivities,
} from "@/cache/documentWorkspaceCache";
import type {
  DocumentGenerationActivity,
  DocumentCareerStage,
  DocumentGenerationLogEntry,
  DocumentGenerationStatus,
  DocumentPackSummary,
  DocumentWorkflowEvent,
  DocumentWorkflowSnapshot,
  DocumentWorkflowStatus,
  DocumentRewriteMode,
} from "@/types/documents";
import { ApiError } from "@/types/api";
import { useUserStore } from "@/stores/user";
import {
  compareUnsignedDecimal,
  maxUnsignedDecimal,
  previousUnsignedDecimal,
} from "@/utils/unsignedDecimal";

const DOCUMENT_PACKS_RETRY_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000] as const;
const DOCUMENT_JOB_POLL_INTERVAL_MS = 800;
const DOCUMENT_GENERATION_LOG_LIMIT = 50;
const DOCUMENT_DRAFT_DEBOUNCE_MS = 300;
const ACTIVE_WORKFLOW_STATUSES = new Set<DocumentWorkflowStatus>([
  "queued",
  "running",
  "recovering",
  "retry_wait",
  "cancelling",
]);
const WORKFLOW_MESSAGES: Record<string, string> = {
  "documents.workflow.ingest": "Preparing your source CV and target role.",
  "documents.workflow.extract_evidence": "Extracting verified evidence from your CV.",
  "documents.workflow.extract_requirements": "Extracting requirements from the target role.",
  "documents.workflow.inputs_ready": "Candidate evidence and role requirements are ready.",
  "documents.workflow.plan": "Planning evidence-backed document changes.",
  "documents.workflow.propose": "Drafting grounded CV and cover-letter proposals.",
  "documents.workflow.verify": "Verifying every proposed claim against your evidence.",
  "documents.workflow.review": "Reviewing the draft for accuracy and relevance.",
  "documents.workflow.render": "Rendering the review-ready documents.",
  "documents.workflow.quality": "Running the final document quality gate.",
  "documents.workflow.completed": "Your document pack is ready for review.",
  "workflow.run.created": "Waiting for a document worker.",
  "workflow.attempt.recovered": "Recovering your document workflow.",
  "workflow.usage_cost_unavailable": "Model usage pricing could not be verified.",
  "workflow.budget_exhausted": "The document workflow reached its configured AI usage budget.",
};

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

async function fileAsBase64(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(new Error("The selected file could not be read."));
    reader.readAsDataURL(file);
  });
}

async function waitForNextJobPoll(signal: AbortSignal): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, DOCUMENT_JOB_POLL_INTERVAL_MS);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

async function waitForRecentPacksRetry(delayMs: number, signal: AbortSignal): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function isRetryableRecentPacksError(caught: unknown): boolean {
  if (typeof caught !== "object" || caught === null || !("status" in caught)) return true;
  const status = (caught as { status?: unknown }).status;
  return typeof status !== "number" || status === 408 || status === 429 || status >= 500;
}

function recentPacksRetryDelay(caught: unknown, fallbackDelayMs: number): number {
  if (
    caught instanceof ApiError
    && caught.status === 429
    && caught.retryAfterMs !== null
  ) {
    return Math.max(fallbackDelayMs, caught.retryAfterMs);
  }
  return fallbackDelayMs;
}

function workflowMessage(code: string | null | undefined, stage: string): string {
  const normalized = String(code ?? "").trim();
  if (WORKFLOW_MESSAGES[normalized]) return WORKFLOW_MESSAGES[normalized];
  if (normalized && (normalized.includes(" ") || !normalized.includes("."))) return normalized;
  const label = stage.replaceAll("_", " ").trim();
  return label ? `${label.replace(/^./, (letter) => letter.toUpperCase())}.` : "Document workflow updated.";
}

function workflowError(code: string | null): string {
  if (!code) return "The documents could not be generated.";
  const messages: Record<string, string> = {
    provider_timeout: "The document service timed out. Your inputs are still available to retry.",
    provider_temporarily_unavailable: "The document service is temporarily unavailable. Please retry.",
    structured_output_invalid: "The generated documents did not pass structural validation. Please retry.",
    "cv.language_normalisation_incomplete":
      "The CV translation was incomplete. Please retry.",
    "cv.language_numeric_facts_changed":
      "The CV translation could not safely preserve every numeric fact. Please retry.",
    "educative.cover_letter_incomplete":
      "The Educative cover letter response was incomplete. Please retry.",
    "educative.heading_coverage_incomplete":
      "The model did not generate headings for every CV bullet. Please retry.",
    "educative.requirement_coverage_incomplete":
      "The model did not cover every analysed job requirement. Please retry.",
    "educative.rewrites_missing":
      "The model did not return any valid Educative CV rewrites. Please retry.",
    "workflow.execution_failed": "The document workflow could not be completed. Please retry.",
    "workflow.input_artifact_missing": "The saved workflow input expired. Start a new generation request.",
    "workflow.quality_gate_failed": "The documents did not pass the final quality gate.",
    "workflow.review_rejected": "The automated reviewer rejected the generated draft.",
    "workflow.storage_unavailable":
      "The documents were generated, but ApplyKitty could not save them to MySQL. Check that the document workflow migrations and worker database grants are current, then retry.",
    "workflow.usage_cost_unavailable":
      "The document service could not verify the model cost. Update the model pricing configuration, then retry.",
    "workflow.budget_exhausted":
      "The document workflow reached its configured AI usage budget. Review the usage limit or model pricing, then retry.",
  };
  return messages[code] ?? "The document workflow could not be completed. Please retry.";
}

export const useDocumentsStore = defineStore("documents", () => {
  const userStore = useUserStore();
  const cvText = ref("");
  const jobDescription = ref("");
  const cvFilename = ref("pasted-cv.txt");
  const cvFileState = ref<"error" | "idle" | "loading" | "selected">("idle");
  const cvTemplate = ref("applykitty-headings");
  const coverLetterTemplate = ref("applykitty-classic");
  const rewriteMode = ref<DocumentRewriteMode>("honest");
  const careerStage = ref<DocumentCareerStage>("graduate");
  const recentPacks = ref<DocumentPackSummary[]>([]);
  const recentPacksError = ref("");
  const loadingPacks = ref(true);
  const extracting = ref(false);
  const generationStatus = ref<DocumentGenerationStatus>("idle");
  const generationMessage = ref("");
  const generationError = ref("");
  const upgradeRequired = ref(false);
  const generationProgress = ref(0);
  const generationLogs = ref<DocumentGenerationLogEntry[]>([]);
  const generationWorkflowId = ref("");
  const generationRevision = ref("0");
  const generationCursor = ref("0");
  const generationCanCancel = ref(false);
  const lastGenerationActivity = ref<DocumentGenerationActivity | null>(null);
  const pendingEditorPackId = ref("");
  const generationActivities = ref<Record<string, DocumentGenerationActivity>>({});
  const generating = computed(() => ["restoring", "running", "cancelling"].includes(
    generationStatus.value,
  ));
  const error = ref("");
  const notice = ref("");
  let listController: AbortController | null = null;
  let operationController: AbortController | null = null;
  let draftTimer: number | null = null;
  let hydratedDraftForUser: string | null = null;
  let hydratedActivitiesForUser: string | null = null;
  const generationEventSequences = new Set<string>();
  let generation = 0;

  function currentDraft(): DocumentFormDraft {
    return {
      coverLetterTemplate: coverLetterTemplate.value,
      careerStage: careerStage.value,
      cvFilename: cvFilename.value,
      cvTemplate: cvTemplate.value,
      cvText: cvText.value,
      jobDescription: jobDescription.value,
      rewriteMode: rewriteMode.value,
    };
  }

  function hasDraftContent(draft: DocumentFormDraft): boolean {
    return Boolean(draft.cvText.trim() || draft.jobDescription.trim() || draft.cvFilename !== "pasted-cv.txt");
  }

  function writeDraft(): void {
    const userId = userStore.user?.id;
    if (!userId || hydratedDraftForUser !== userId) return;
    const draft = currentDraft();
    writeDocumentDraft(userId, hasDraftContent(draft) ? draft : null);
  }

  function flushDraft(): void {
    if (draftTimer !== null) {
      window.clearTimeout(draftTimer);
      draftTimer = null;
    }
    writeDraft();
  }

  function scheduleDraft(): void {
    if (draftTimer !== null) window.clearTimeout(draftTimer);
    draftTimer = window.setTimeout(() => {
      draftTimer = null;
      writeDraft();
    }, DOCUMENT_DRAFT_DEBOUNCE_MS);
  }

  function hydrateDraft(): void {
    const userId = userStore.user?.id;
    if (!userId || hydratedDraftForUser === userId) return;
    hydratedDraftForUser = userId;
    if (hasDraftContent(currentDraft())) return;
    const draft = readDocumentDraft(userId);
    if (!draft) return;
    coverLetterTemplate.value = draft.coverLetterTemplate;
    careerStage.value = draft.careerStage;
    cvFilename.value = draft.cvFilename;
    cvTemplate.value = draft.cvTemplate;
    cvText.value = draft.cvText;
    jobDescription.value = draft.jobDescription;
    rewriteMode.value = draft.rewriteMode;
    cvFileState.value = cvText.value ? "selected" : "idle";
  }

  function hydrateGenerationActivities(): void {
    const userId = userStore.user?.id;
    if (!userId || hydratedActivitiesForUser === userId) return;
    const activities = readDocumentGenerationActivities(userId);
    generationActivities.value = Object.fromEntries(
      activities.map((activity) => [activity.packId, activity]),
    );
    lastGenerationActivity.value = activities[0] ?? null;
    hydratedActivitiesForUser = userId;
  }

  function generationActivityForPack(packId: string): DocumentGenerationActivity | null {
    hydrateGenerationActivities();
    return generationActivities.value[packId]
      ?? (lastGenerationActivity.value?.packId === packId ? lastGenerationActivity.value : null);
  }

  function rememberGenerationActivity(activity: DocumentGenerationActivity): void {
    hydrateGenerationActivities();
    generationActivities.value = {
      ...generationActivities.value,
      [activity.packId]: activity,
    };
    lastGenerationActivity.value = activity;
    const userId = userStore.user?.id;
    if (!userId) return;
    writeDocumentGenerationActivities(userId, [
      activity,
      ...Object.values(generationActivities.value).filter((item) => item.packId !== activity.packId),
    ]);
  }

  function acknowledgeEditorNavigation(packId: string): void {
    if (pendingEditorPackId.value === packId) pendingEditorPackId.value = "";
  }

  watch(
    [careerStage, coverLetterTemplate, cvFilename, cvTemplate, cvText, jobDescription, rewriteMode],
    scheduleDraft,
    { flush: "post" },
  );

  function resetWorkflowDisplay(): void {
    upgradeRequired.value = false;
    generationStatus.value = "idle";
    generationMessage.value = "";
    generationError.value = "";
    generationProgress.value = 0;
    generationLogs.value = [];
    generationWorkflowId.value = "";
    generationRevision.value = "0";
    generationCursor.value = "0";
    generationCanCancel.value = false;
    generationEventSequences.clear();
  }

  function appendWorkflowEvent(event: DocumentWorkflowEvent): void {
    if (generationEventSequences.has(event.sequence)) return;
    generationEventSequences.add(event.sequence);
    generationLogs.value = [
      ...generationLogs.value,
      {
        stage: event.stage,
        progress: Math.max(0, Math.min(100, Math.round(event.progress))),
        message: workflowMessage(event.message_code, event.stage),
        updatedAt: event.occurred_at,
      },
    ].slice(-DOCUMENT_GENERATION_LOG_LIMIT);
  }

  function applyWorkflowSnapshot(snapshot: DocumentWorkflowSnapshot): boolean {
    const changedWorkflow = generationWorkflowId.value !== snapshot.id;
    if (
      !changedWorkflow
      && compareUnsignedDecimal(snapshot.revision, generationRevision.value) < 0
    ) return false;
    if (changedWorkflow) {
      resetWorkflowDisplay();
      generationWorkflowId.value = snapshot.id;
    }
    generationRevision.value = maxUnsignedDecimal(
      generationRevision.value,
      snapshot.revision,
    );
    generationCursor.value = maxUnsignedDecimal(
      generationCursor.value,
      previousUnsignedDecimal(snapshot.next_sequence),
      ...snapshot.events.map((event) => event.sequence),
    );
    generationProgress.value = Math.max(
      generationProgress.value,
      Math.max(0, Math.min(100, Math.round(snapshot.progress))),
    );
    generationCanCancel.value = snapshot.can_cancel;
    snapshot.events
      .slice()
      .sort((left, right) => compareUnsignedDecimal(left.sequence, right.sequence))
      .forEach(appendWorkflowEvent);
    const latestEvent = snapshot.events
      .slice()
      .sort((left, right) => compareUnsignedDecimal(right.sequence, left.sequence))[0];
    generationMessage.value = workflowMessage(
      latestEvent?.message_code ?? snapshot.error_code,
      snapshot.stage,
    );
    if (snapshot.status === "completed") generationStatus.value = "succeeded";
    else if (snapshot.status === "failed") generationStatus.value = "failed";
    else if (snapshot.status === "cancelled") generationStatus.value = "cancelled";
    else if (snapshot.status === "cancelling") generationStatus.value = "cancelling";
    else generationStatus.value = "running";
    generationError.value = snapshot.status === "failed"
      ? workflowError(snapshot.error_code)
      : "";
    return true;
  }

  async function loadRecentPacks(): Promise<void> {
    listController?.abort();
    const controller = new AbortController();
    listController = controller;
    const requestGeneration = ++generation;
    loadingPacks.value = recentPacks.value.length === 0;
    recentPacksError.value = "";
    try {
      let packs: DocumentPackSummary[] | undefined;
      for (let attempt = 0; packs === undefined; attempt += 1) {
        try {
          packs = await listDocumentPacks(controller.signal);
        } catch (caught: unknown) {
          if (controller.signal.aborted || isAbortError(caught)) throw caught;
          const retryDelay = DOCUMENT_PACKS_RETRY_DELAYS_MS[attempt];
          if (retryDelay === undefined || !isRetryableRecentPacksError(caught)) throw caught;
          await waitForRecentPacksRetry(
            recentPacksRetryDelay(caught, retryDelay),
            controller.signal,
          );
        }
      }
      if (listController !== controller || generation !== requestGeneration || controller.signal.aborted) return;
      recentPacks.value = packs;
    } catch (caught: unknown) {
      if (listController !== controller || generation !== requestGeneration || isAbortError(caught)) return;
      recentPacksError.value = caught instanceof Error ? caught.message : "Recent documents are unavailable.";
    } finally {
      if (listController === controller) {
        loadingPacks.value = false;
        listController = null;
      }
    }
  }

  async function handleCvFile(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    operationController?.abort();
    const controller = new AbortController();
    operationController = controller;
    extracting.value = true;
    cvFileState.value = "loading";
    error.value = "";
    notice.value = "";
    cvFilename.value = file.name;
    try {
      if (file.name.toLowerCase().endsWith(".pdf")) {
        const result = await extractPdfText(await fileAsBase64(file), controller.signal);
        if (operationController !== controller || controller.signal.aborted) return;
        cvText.value = result.text;
        if (result.repaired) {
          const pages = result.repaired_pages ?? 0;
          const messages: string[] = [];
          if (result.structure_repaired) {
            messages.push("Rebuilt the PDF structure before extracting your CV.");
          }
          if (pages) {
            messages.push(`Recovered fragmented words on ${pages} PDF page${pages === 1 ? "" : "s"}.`);
          }
          notice.value = messages.join(" ");
        }
      } else {
        const text = await file.text();
        if (operationController !== controller || controller.signal.aborted) return;
        cvText.value = text;
      }
      cvFileState.value = "selected";
    } catch (caught: unknown) {
      if (operationController !== controller || isAbortError(caught)) return;
      error.value = caught instanceof Error ? caught.message : "The CV could not be read.";
      cvFileState.value = "error";
    } finally {
      if (operationController === controller) {
        extracting.value = false;
        operationController = null;
      }
    }
  }

  async function trackWorkflow(
    initial: DocumentWorkflowSnapshot,
    controller: AbortController,
  ): Promise<string | null> {
    let snapshot = initial;
    while (true) {
      if (operationController !== controller || controller.signal.aborted) return null;
      if (!applyWorkflowSnapshot(snapshot)) {
        await waitForNextJobPoll(controller.signal);
        snapshot = await getDocumentWorkflow(
          snapshot.id,
          generationCursor.value,
          controller.signal,
        );
        continue;
      }
      if (!ACTIVE_WORKFLOW_STATUSES.has(snapshot.status)) break;
      await waitForNextJobPoll(controller.signal);
      snapshot = await getDocumentWorkflow(
        snapshot.id,
        generationCursor.value,
        controller.signal,
      );
    }

    if (snapshot.status === "failed") return null;
    if (snapshot.status === "cancelled") {
      notice.value = "Document generation was cancelled.";
      return null;
    }
    const packId = snapshot.document_pack_id;
    if (!packId) {
      generationStatus.value = "failed";
      generationError.value = "Generation completed without a document pack.";
      return null;
    }
    const completedAt = snapshot.events
      .slice()
      .sort((left, right) => compareUnsignedDecimal(right.sequence, left.sequence))[0]?.occurred_at
      ?? new Date().toISOString();
    generationStatus.value = "succeeded";
    rememberGenerationActivity({
      completedAt,
      logs: generationLogs.value.map((entry) => ({ ...entry })),
      message: generationMessage.value,
      packId,
    });
    pendingEditorPackId.value = packId;
    return packId;
  }

  async function generate(): Promise<string | null> {
    error.value = "";
    upgradeRequired.value = false;
    resetWorkflowDisplay();
    pendingEditorPackId.value = "";
    if (!cvText.value.trim() || !jobDescription.value.trim()) {
      error.value = "Add your source CV and the target job description.";
      return null;
    }
    operationController?.abort();
    const controller = new AbortController();
    operationController = controller;
    notice.value = "";
    generationStatus.value = "running";
    generationMessage.value = "Preparing your tailored CV and cover letter.";
    try {
      const workflow = await createDocumentWorkflow({
        cv_filename: cvFilename.value,
        cv_text: cvText.value,
        job_description: jobDescription.value,
        cv_template_id: cvTemplate.value,
        cover_letter_template_id: coverLetterTemplate.value,
        rewrite_mode: rewriteMode.value,
        career_stage: careerStage.value,
      }, controller.signal);
      return await trackWorkflow(workflow, controller);
    } catch (caught: unknown) {
      if (operationController !== controller || isAbortError(caught)) return null;
      generationStatus.value = "failed";
      generationMessage.value = "Generation stopped before completion.";
      generationError.value = caught instanceof Error
        ? caught.message
        : "The documents could not be generated.";
      upgradeRequired.value = caught instanceof ApiError
        && caught.code === "billing.upgrade_required";
      return null;
    } finally {
      if (operationController === controller) operationController = null;
    }
  }

  async function restoreActiveWorkflow(): Promise<string | null> {
    if (!userStore.user?.id || generating.value || generationWorkflowId.value) return null;
    operationController?.abort();
    const controller = new AbortController();
    operationController = controller;
    generationStatus.value = "restoring";
    generationMessage.value = "Checking for an active document workflow.";
    generationError.value = "";
    try {
      const workflow = await getActiveDocumentWorkflow(controller.signal);
      if (operationController !== controller || controller.signal.aborted) return null;
      if (!workflow) {
        resetWorkflowDisplay();
        return null;
      }
      return await trackWorkflow(workflow, controller);
    } catch (caught: unknown) {
      if (operationController !== controller || isAbortError(caught)) return null;
      generationStatus.value = "failed";
      generationMessage.value = "Active workflow recovery failed.";
      generationError.value = caught instanceof Error
        ? caught.message
        : "The active document workflow could not be restored.";
      return null;
    } finally {
      if (operationController === controller) operationController = null;
    }
  }

  async function cancelGeneration(): Promise<void> {
    const workflowId = generationWorkflowId.value;
    if (!workflowId || !generationCanCancel.value || !generating.value) return;
    operationController?.abort();
    const controller = new AbortController();
    operationController = controller;
    generationStatus.value = "cancelling";
    generationMessage.value = "Requesting workflow cancellation.";
    generationError.value = "";
    try {
      const workflow = await cancelDocumentWorkflow(workflowId, controller.signal);
      await trackWorkflow(workflow, controller);
    } catch (caught: unknown) {
      if (operationController !== controller || isAbortError(caught)) return;
      generationStatus.value = "running";
      const cancellationError = caught instanceof Error
        ? caught.message
        : "Cancellation could not be confirmed. The workflow is still active.";
      try {
        const activeWorkflow = await getActiveDocumentWorkflow(controller.signal);
        if (operationController !== controller || controller.signal.aborted) return;
        if (activeWorkflow?.id === workflowId) {
          notice.value = "Cancellation could not be confirmed. Workflow tracking resumed.";
          generationError.value = "";
          await trackWorkflow(activeWorkflow, controller);
          return;
        }
      } catch (recoveryError: unknown) {
        if (operationController !== controller || isAbortError(recoveryError)) return;
      }
      generationStatus.value = "failed";
      generationMessage.value = "Workflow status could not be restored.";
      generationError.value = cancellationError;
    } finally {
      if (operationController === controller) operationController = null;
    }
  }

  function dismissGeneration(): void {
    if (generating.value) return;
    resetWorkflowDisplay();
  }

  function dispose(): void {
    flushDraft();
    generation += 1;
    listController?.abort();
    operationController?.abort();
    listController = null;
    operationController = null;
    loadingPacks.value = false;
    extracting.value = false;
    resetWorkflowDisplay();
  }

  function reset(): void {
    dispose();
    lastGenerationActivity.value = null;
    pendingEditorPackId.value = "";
    generationActivities.value = {};
    hydratedActivitiesForUser = null;
    hydratedDraftForUser = null;
    clearDocumentWorkspaceCaches();
    cvText.value = "";
    jobDescription.value = "";
    cvFilename.value = "pasted-cv.txt";
    cvFileState.value = "idle";
    cvTemplate.value = "applykitty-headings";
    coverLetterTemplate.value = "applykitty-classic";
    rewriteMode.value = "honest";
    careerStage.value = "graduate";
    recentPacks.value = [];
    recentPacksError.value = "";
    error.value = "";
    notice.value = "";
    upgradeRequired.value = false;
  }

  return {
    acknowledgeEditorNavigation,
    cancelGeneration,
    coverLetterTemplate,
    careerStage,
    cvFilename,
    cvFileState,
    cvTemplate,
    cvText,
    dispose,
    dismissGeneration,
    error,
    extracting,
    generate,
    generationActivityForPack,
    generationCanCancel,
    generationCursor,
    generationError,
    upgradeRequired,
    generationLogs,
    generationMessage,
    generationProgress,
    generationStatus,
    generationWorkflowId,
    hydrateGenerationActivities,
    lastGenerationActivity,
    generating,
    handleCvFile,
    hydrateDraft,
    jobDescription,
    loadingPacks,
    loadRecentPacks,
    notice,
    pendingEditorPackId,
    recentPacks,
    recentPacksError,
    rewriteMode,
    reset,
    restoreActiveWorkflow,
    flushDraft,
  };
});
